import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import { EmailAuthProvider, GoogleAuthProvider, reauthenticateWithCredential, reauthenticateWithPopup } from 'firebase/auth';
import { ChevronLeft, ChevronRight, CreditCard, Shield, SlidersHorizontal, Trophy, UserCircle2 } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { cn } from '../lib/utils';
import { useLanguage } from '../functions/useLanguage';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useBean } from '../context/BeanContext';
import { db, app } from '../lib/firebase';
import { AppSelect } from '../components/AppSelect';
import { DeleteAccountModal } from '../components/DeleteAccountModal';
import { ReminderSettings } from '../components/ReminderSettings';
import { setSavedLanguage, LANGUAGE_OPTIONS, type SupportedLanguage } from '../functions/languageSettings';
import { useStripeSubscription } from '../hooks/useStripeSubscription';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { MEDICAL_PROFILE_EVENT, MEDICAL_PROFILE_KEY, loadMedicalProfile, saveMedicalProfile } from './Library/MedicalCard';
import { usePet } from '../context/PetContext';

type NotificationSetting = 'all' | 'logs' | 'meds' | 'none';
type SettingsSection = 'profile' | 'app-preferences' | 'subscription' | 'security';

function saveLocalSettings(updates: Record<string, unknown>) {
  const current = JSON.parse(localStorage.getItem('koda_settings') || '{}');
  localStorage.setItem('koda_settings', JSON.stringify({ ...current, ...updates }));
  window.dispatchEvent(new Event('storage'));
}

function TimeInput({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const [hours = '08', minutes = '00'] = value.split(':');

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-400 w-8 shrink-0">{label}</span>
      <div className="flex items-center bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <select
          value={hours}
          onChange={e => onChange(`${e.target.value}:${minutes}`)}
          className="text-sm font-semibold text-text-secondary bg-white px-2 py-2 outline-none appearance-none cursor-pointer hover:bg-gray-50 transition-colors border-r border-gray-100"
        >
          {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(hour => (
            <option key={hour} value={hour}>{hour}</option>
          ))}
        </select>
        <span className="text-sm font-bold text-gray-400 px-0.5">:</span>
        <select
          value={minutes}
          onChange={e => onChange(`${hours}:${e.target.value}`)}
          className="text-sm font-semibold text-text-secondary bg-white px-2 py-2 outline-none appearance-none cursor-pointer hover:bg-gray-50 transition-colors"
        >
          {['00', '15', '30', '45'].map(min => (
            <option key={min} value={min}>{min}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function SettingsRow({ label, children, align = 'center' }: { label: string; children: React.ReactNode; align?: 'center' | 'start' }) {
  return (
    <div className={cn('flex gap-4 py-4 border-b border-gray-50 last:border-0', align === 'start' ? 'items-start' : 'items-center')}>
      <span className="text-sm text-text-secondary w-40 shrink-0">{label}</span>
      <div className="flex-1 flex flex-wrap items-center justify-end gap-2">{children}</div>
    </div>
  );
}

function SectionLayout({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  const navigate = useNavigate();

  return (
    <div className="w-full space-y-4">
      <div className="mb-2">
        <button
          type="button"
          onClick={() => navigate('/settings')}
          className="inline-flex items-center gap-2 text-sm font-semibold text-primary-brown hover:text-primary-brown/80 transition-colors mb-3"
        >
          <ChevronLeft size={16} />
          Settings
        </button>
        <h1 className="text-2xl font-black text-[#0d2744]">{title}</h1>
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      </div>
      {children}
    </div>
  );
}

function ProfileSettings() {
  const { t } = useLanguage();
  const { isAuthenticated, user, firebaseUser, updateUser } = useAuth();
  const { activePet, activePetId, updatePet, isActivePetSubordinate } = usePet();

  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSaved, setProfileSaved] = useState(false);

  const [profileName, setProfileName] = useState('');
  const [profession, setProfession] = useState('');
  const [transplantDate, setTransplantDate] = useState('');
  const [dob, setDob] = useState('');
  const [hospitalName, setHospitalName] = useState('');
  const [processNumber, setProcessNumber] = useState('');
  const [allergies, setAllergies] = useState('');
  const [surgeon, setSurgeon] = useState('');

  useEffect(() => {
    const local = loadMedicalProfile(activePetId);
    setProfileName(local.fullName || '');
    setProfession(activePet?.name || local.profession || '');
    setTransplantDate(activePet?.dob || local.transplantDate || '');
    setDob(activePet?.breed || local.dob || '');
    setHospitalName(local.hospitalName || '');
    setProcessNumber(local.processNumber || '');
    setAllergies(local.allergies || '');
    setSurgeon(local.surgeon || '');
  }, [activePet?.name, activePet?.dob, activePet?.breed, activePetId]);

  useEffect(() => {
    if (!user) return;
    setProfileName(user.full_name || '');
    setProfession(activePet?.name || user.profession || '');
    setTransplantDate(activePet?.dob || user.transplant_date || '');
    setDob(activePet?.breed || user.dob || '');
    setHospitalName(activePet?.vet_practice || user.hospital_name || '');
    setProcessNumber(activePet?.microchip || user.process_number || '');
    setAllergies(activePet?.allergies || user.allergies || '');
    setSurgeon(activePet?.primary_vet || user.surgeon || '');
  }, [activePet?.name, activePet?.dob, activePet?.breed, activePet?.vet_practice, activePet?.microchip, activePet?.allergies, activePet?.primary_vet, user?.full_name, user?.profession, user?.transplant_date, user?.dob, user?.hospital_name, user?.process_number, user?.allergies, user?.surgeon]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      const scopedKey = activePetId ? `${MEDICAL_PROFILE_KEY}_${activePetId}` : MEDICAL_PROFILE_KEY;
      if (event.key !== scopedKey && event.key !== MEDICAL_PROFILE_KEY) return;
      const next = loadMedicalProfile(activePetId);
      setProfileName(next.fullName || '');
      setProfession(activePet?.name || next.profession || '');
      setTransplantDate(activePet?.dob || next.transplantDate || '');
      setDob(activePet?.breed || next.dob || '');
      setHospitalName(next.hospitalName || '');
      setProcessNumber(next.processNumber || '');
      setAllergies(next.allergies || '');
      setSurgeon(next.surgeon || '');
    };

    const onMedicalProfile = (event: Event) => {
      const custom = event as CustomEvent<{ petId?: string | null }>;
      if (custom.detail?.petId && activePetId && custom.detail.petId !== activePetId) return;
      const next = loadMedicalProfile(activePetId);
      setProfileName(next.fullName || '');
      setProfession(activePet?.name || next.profession || '');
      setTransplantDate(activePet?.dob || next.transplantDate || '');
      setDob(activePet?.breed || next.dob || '');
      setHospitalName(next.hospitalName || '');
      setProcessNumber(next.processNumber || '');
      setAllergies(next.allergies || '');
      setSurgeon(next.surgeon || '');
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(MEDICAL_PROFILE_EVENT, onMedicalProfile as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(MEDICAL_PROFILE_EVENT, onMedicalProfile as EventListener);
    };
  }, [activePet?.breed, activePet?.dob, activePet?.name, activePetId]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setProfileError('');
    setProfileSaved(false);

    try {
      if (isActivePetSubordinate) {
        // Subordinates may only update their own display name
        await updateUser({ full_name: profileName });
      } else {
        saveMedicalProfile({
          fullName: profileName,
          profession,
          transplantDate,
          dob,
          hospitalName,
          processNumber,
          allergies,
          surgeon,
        }, activePetId);

        if (activePetId) {
          const petUpdates: Record<string, string> = {
            name: profession || activePet?.name || 'Pet',
          };
          if (dob) petUpdates.breed = dob;
          if (transplantDate) petUpdates.dob = transplantDate;
          if (hospitalName) petUpdates.vet_practice = hospitalName;
          if (processNumber) petUpdates.microchip = processNumber;
          if (allergies) petUpdates.allergies = allergies;
          if (surgeon) petUpdates.primary_vet = surgeon;
          await updatePet(activePetId, petUpdates);
        }

        await updateUser({
          full_name: profileName,
          hospital_name: hospitalName || undefined,
          process_number: processNumber || undefined,
          allergies: allergies || undefined,
          surgeon: surgeon || undefined,
        });
      }
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2500);
    } catch {
      setProfileError(t('account.profileSaveError'));
    } finally {
      setSavingProfile(false);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (firebaseUser && !firebaseUser.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  return (
    <SectionLayout title={t('account.profile')} description={t('medcard.hint')}>
      <section className="bg-white border border-slate-100 rounded-3xl p-7">
        {profileError && (
          <div className="mb-4 px-4 py-2.5 rounded-2xl bg-error-red\/5 border border-error-red\/20 text-error-red text-sm">
            {profileError}
          </div>
        )}
        {profileSaved && (
          <div className="mb-4 px-4 py-2.5 rounded-2xl bg-green-50 border border-green-200 text-green-700 text-sm">
            {t('account.profileSaved')}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm text-gray-500 mb-1">{t('medcard.fullName')}</label>
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-brown/15 flex items-center justify-center text-primary-brown font-black text-base tracking-tight select-none">
                {profileName
                  ? profileName.trim().split(/\s+/).slice(0, 2).map(word => word[0].toUpperCase()).join('')
                  : '?'}
              </div>
              <input
                value={profileName}
                onChange={e => setProfileName(e.target.value)}
                placeholder={t('medcard.placeholder.name')}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none focus:ring-2 focus:ring-primary-brown/30"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">{t('medcard.profession')}</label>
            <input
              value={profession}
              onChange={e => setProfession(e.target.value)}
              placeholder={t('medcard.placeholder.profession')}
              readOnly={isActivePetSubordinate}
              className={cn(
                'w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none',
                isActivePetSubordinate ? 'bg-gray-50 text-gray-400 cursor-default' : 'focus:ring-2 focus:ring-primary-brown/30'
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <label className="block text-sm text-gray-500 mb-1">{t('medcard.dob')}</label>
            <input
              value={dob}
              onChange={e => setDob(e.target.value)}
              placeholder={t('medcard.placeholder.dob')}
              readOnly={isActivePetSubordinate}
              className={cn(
                'w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none',
                isActivePetSubordinate ? 'bg-gray-50 text-gray-400 cursor-default' : 'focus:ring-2 focus:ring-primary-brown/30'
              )}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">{t('medcard.transplantDate')}</label>
            <input
              value={transplantDate}
              onChange={e => setTransplantDate(e.target.value)}
              placeholder="MM/DD/YYYY"
              readOnly={isActivePetSubordinate}
              className={cn(
                'w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none',
                isActivePetSubordinate ? 'bg-gray-50 text-gray-400 cursor-default' : 'focus:ring-2 focus:ring-primary-brown/30'
              )}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">{t('medcard.hospital')}</label>
            <input
              value={hospitalName}
              onChange={e => setHospitalName(e.target.value)}
              placeholder={t('medcard.placeholder.hospital')}
              readOnly={isActivePetSubordinate}
              className={cn(
                'w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none',
                isActivePetSubordinate ? 'bg-gray-50 text-gray-400 cursor-default' : 'focus:ring-2 focus:ring-primary-brown/30'
              )}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">{t('medcard.processNo')}</label>
            <input
              value={processNumber}
              onChange={e => setProcessNumber(e.target.value)}
              placeholder={t('medcard.placeholder.processNo')}
              readOnly={isActivePetSubordinate}
              className={cn(
                'w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none',
                isActivePetSubordinate ? 'bg-gray-50 text-gray-400 cursor-default' : 'focus:ring-2 focus:ring-primary-brown/30'
              )}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">{t('medcard.surgeon')}</label>
            <input
              value={surgeon}
              onChange={e => setSurgeon(e.target.value)}
              placeholder={t('medcard.placeholder.surgeon')}
              readOnly={isActivePetSubordinate}
              className={cn(
                'w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none',
                isActivePetSubordinate ? 'bg-gray-50 text-gray-400 cursor-default' : 'focus:ring-2 focus:ring-primary-brown/30'
              )}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">{t('medcard.allergies')}</label>
            <input
              value={allergies}
              onChange={e => setAllergies(e.target.value)}
              placeholder={t('medcard.placeholder.allergies')}
              readOnly={isActivePetSubordinate}
              className={cn(
                'w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none',
                isActivePetSubordinate ? 'bg-gray-50 text-gray-400 cursor-default' : 'focus:ring-2 focus:ring-primary-brown/30'
              )}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <button
            onClick={handleSaveProfile}
            disabled={savingProfile}
            className="text-sm font-bold bg-primary-brown hover:bg-primary-brown/80 text-white px-5 py-2 rounded-full transition-colors disabled:opacity-70"
          >
            {savingProfile ? t('account.savingProfile') : t('account.saveProfile')}
          </button>
        </div>
      </section>
    </SectionLayout>
  );
}

function AppPreferencesSettings() {
  const { language, t } = useLanguage();
  const { isActivePetSubordinate } = usePet();

  const handleReplayWalkthrough = () => {
    window.dispatchEvent(new Event('koda:replay-walkthrough'));
  };
  const [metric, setMetric] = useState(true);
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('12h');

  useEffect(() => {
    try {
      const saved = localStorage.getItem('koda_settings');
      if (!saved) return;
      const settings = JSON.parse(saved);
      if (settings.metric !== undefined) setMetric(Boolean(settings.metric));
      if (settings.dateFormat) setDateFormat(settings.dateFormat);
      if (settings.timeFormat === '12h' || settings.timeFormat === '24h') setTimeFormat(settings.timeFormat);
    } catch {
      // ignore malformed localStorage
    }
  }, []);

  return (
    <SectionLayout title={t('options.appPreferences')} description="">
      <section className="bg-white border border-slate-100 rounded-3xl p-7">
        <h2 className="text-lg font-bold text-[#0d2744] mb-5">{t('options.appPreferences')}</h2>
        <div className="text-sm">
          <div className="flex items-center justify-between py-4 border-b border-gray-50">
            <span className="text-sm text-gray-500 shrink-0">{t('options.dateFormat')}</span>
            <AppSelect
              value={dateFormat}
              onChange={value => { setDateFormat(value); saveLocalSettings({ dateFormat: value }); }}
              options={['MM/DD/YYYY', 'DD/MM/YYYY', 'DD/Month/YYYY', 'YYYY-MM-DD', 'Month Day, YYYY']}
            />
          </div>

          <div className="flex items-center justify-between py-4 border-b border-gray-50">
            <span className="text-sm text-gray-500 shrink-0">{t('options.timeFormat')}</span>
            <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
              <button
                onClick={() => { setTimeFormat('12h'); saveLocalSettings({ timeFormat: '12h' }); }}
                className={cn('px-3 py-1 rounded-md text-xs font-medium transition-all', timeFormat === '12h' ? 'bg-white shadow-sm text-primary-brown' : 'text-gray-500')}
              >12h</button>
              <button
                onClick={() => { setTimeFormat('24h'); saveLocalSettings({ timeFormat: '24h' }); }}
                className={cn('px-3 py-1 rounded-md text-xs font-medium transition-all', timeFormat === '24h' ? 'bg-white shadow-sm text-primary-brown' : 'text-gray-500')}
              >24h</button>
            </div>
          </div>

          <div className="flex items-center justify-between py-4 border-b border-gray-50">
            <span className="text-sm text-gray-500 shrink-0">{t('options.units')}</span>
            <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
              <button
                onClick={() => { setMetric(true); saveLocalSettings({ metric: true }); }}
                className={cn('px-3 py-1 rounded-md text-xs font-medium transition-all', metric ? 'bg-white shadow-sm text-primary-brown' : 'text-gray-500')}
              >{t('options.metric')}</button>
              <button
                onClick={() => { setMetric(false); saveLocalSettings({ metric: false }); }}
                className={cn('px-3 py-1 rounded-md text-xs font-medium transition-all', !metric ? 'bg-white shadow-sm text-primary-brown' : 'text-gray-500')}
              >{t('options.imperial')}</button>
            </div>
          </div>

          {/* Replay Walkthrough */}
          <div className="flex items-center justify-between py-4">
            <span className="text-gray-700">{t('options.replayWalkthrough')}</span>
            <button
              onClick={handleReplayWalkthrough}
              className="px-4 py-1.5 rounded-full bg-primary-brown text-white text-xs font-bold hover:opacity-90 transition-all"
            >
              ▶
            </button>
          </div>
        </div>
      </section>

      {!isActivePetSubordinate && (
        <section className="bg-white border border-slate-100 rounded-3xl p-7">
          <h2 className="text-lg font-bold text-[#0d2744] mb-5">{t('guide.category.reminders.title')}</h2>
          <ReminderSettings />
        </section>
      )}
    </SectionLayout>
  );
}

function SubscriptionSettings() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { subscription, loading: subscriptionLoading } = useStripeSubscription(user?.id);
  const trialStatus = useTrialStatus();

  const formatDate = (value?: string) => {
    if (!value) return '—';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
  };

  const humanizeStatus = (value?: string) => {
    if (!value) return t('options.subscriptionNoActive');
    return value
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  const handleManagePlans = () => {
    navigate('/website');
    setTimeout(() => {
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
    }, 120);
  };

  const isLoading = subscriptionLoading || trialStatus.phase === 'loading';
  const planName = subscription?.planName ?? (trialStatus.phase !== 'expired' ? t('account.starterTrial') : t('account.expired'));
  const isLifetimePlan = (() => {
    const planId = (subscription?.planId ?? '').toLowerCase();
    if (planId === 'lifetime') return true;
    const name = (subscription?.planName ?? '').toLowerCase();
    return name.includes('lifetime') || name.includes('expert companion');
  })();

  const statusLabel = (() => {
    if (isLoading) return t('account.loading');
    if (trialStatus.hasActiveSubscription) return humanizeStatus(subscription?.status);
    if (trialStatus.phase === 'warning') {
      return t('account.trialDaysLeft')
        .replace('{days}', String(trialStatus.trialDaysLeft))
        .replace('{s}', trialStatus.trialDaysLeft === 1 ? '' : 's');
    }
    if (trialStatus.phase === 'expired_grace') {
      return t('account.expiredDaysLeft')
        .replace('{days}', String(trialStatus.graceDaysLeft))
        .replace('{s}', trialStatus.graceDaysLeft === 1 ? '' : 's');
    }
    if (trialStatus.phase === 'expired') return t('account.expired');
    if (trialStatus.phase === 'active' && !trialStatus.hasActiveSubscription) {
      return t('account.trialDaysLeft')
        .replace('{days}', String(trialStatus.trialDaysLeft))
        .replace('{s}', trialStatus.trialDaysLeft === 1 ? '' : 's');
    }
    return humanizeStatus(subscription?.status);
  })();

  const cardColour =
    trialStatus.phase === 'expired' || trialStatus.phase === 'expired_grace'
      ? 'border-error-red\/10 bg-error-red\/5/60'
      : trialStatus.phase === 'warning'
      ? 'border-amber-100 bg-amber-50/60'
      : 'border-primary-brown/20 bg-primary-brown/10';

  const labelColour =
    trialStatus.phase === 'expired' || trialStatus.phase === 'expired_grace'
      ? 'text-error-red'
      : trialStatus.phase === 'warning'
      ? 'text-amber-600'
      : 'text-primary-brown';

  return (
    <SectionLayout title={t('options.subscription')} description={t('options.subscriptionSubtitle')}>
      <section className="bg-white border border-slate-100 rounded-3xl p-7">
        <div className={cn('rounded-2xl border p-5 mb-6', cardColour)}>
          <p className={cn('text-xs uppercase tracking-widest font-bold mb-2', labelColour)}>{t('account.currentPlan')}</p>
          <p className="text-2xl font-black text-[#0d2744]">{isLoading ? t('account.loading') : planName}</p>
          <p className="text-sm text-gray-600 mt-2">{isLoading ? t('account.fetchingBilling') : statusLabel}</p>
        </div>

        {subscription && (
          <dl className="space-y-4 text-sm mb-6">
            <div>
              <dt className="text-gray-500">{t('options.subscriptionStarted')}</dt>
              <dd className="text-[#0d2744] font-semibold">{formatDate(subscription.startedAt)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">{t('options.subscriptionNextPayment')}</dt>
              <dd className="text-[#0d2744] font-semibold">{formatDate(subscription.nextPaymentAt)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">{t('options.subscriptionExpires')}</dt>
              <dd className="text-[#0d2744] font-semibold">{formatDate(subscription.expiresAt)}</dd>
            </div>
          </dl>
        )}

        {!isLifetimePlan && (
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleManagePlans}
              className="text-sm font-bold bg-primary-brown hover:bg-primary-brown/80 text-white px-5 py-2 rounded-full transition-colors"
            >
              {t('trial.upgrade')}
            </button>
          </div>
        )}
      </section>
    </SectionLayout>
  );
}

function SecuritySettings() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, user, firebaseUser, logout, sendPasswordReset, requestEmailChange, deleteAccount } = useAuth();
  const { logs, medications, contacts } = useData();
  const { profile, xpEvents, roomieFeed } = useBean();
  const [exporting, setExporting] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [emailChanging, setEmailChanging] = useState(false);
  const [emailChangeDraft, setEmailChangeDraft] = useState('');
  const [emailChangeSent, setEmailChangeSent] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState('');
  const [pendingEmailChange, setPendingEmailChange] = useState('');
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [reauthError, setReauthError] = useState('');
  const [reauthLoading, setReauthLoading] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (firebaseUser && !firebaseUser.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  const createdAt = firebaseUser?.metadata?.creationTime ? new Date(firebaseUser.metadata.creationTime).toLocaleDateString() : '—';
  const lastLoginAt = firebaseUser?.metadata?.lastSignInTime ? new Date(firebaseUser.metadata.lastSignInTime).toLocaleDateString() : '—';

  const handleLogout = async () => {
    await logout();
    navigate('/website');
  };

  const handleResetPassword = async () => {
    setPasswordResetSent(false);
    try {
      await sendPasswordReset();
      setPasswordResetSent(true);
    } catch {
      alert(t('account.resetPasswordEmailError'));
    }
  };

  const handleChangeEmail = async () => {
    const nextEmail = emailChangeDraft.trim();
    if (!nextEmail) return;
    setEmailChanging(true);
    setEmailChangeSent(false);
    setEmailChangeError('');
    try {
      await requestEmailChange(nextEmail);
      setEmailChangeSent(true);
      setEmailChangeDraft('');
    } catch (err) {
      if (err instanceof FirebaseError) {
        if (err.code === 'auth/requires-recent-login') {
          setPendingEmailChange(nextEmail);
          setReauthPassword('');
          setReauthError('');
          setShowReauthModal(true);
        } else if (err.code === 'auth/email-already-in-use') {
          setEmailChangeError('That email is already in use.');
        } else if (err.code === 'auth/invalid-email') {
          setEmailChangeError('Please enter a valid email address.');
        } else {
          setEmailChangeError('Could not start email change. Please try again.');
        }
      } else {
        setEmailChangeError('Could not start email change. Please try again.');
      }
    } finally {
      setEmailChanging(false);
    }
  };

  const hasPasswordProvider = !!firebaseUser?.providerData?.some(p => p.providerId === 'password');

  const handleReauthenticateAndRetry = async () => {
    if (!firebaseUser || !pendingEmailChange) return;
    setReauthLoading(true);
    setReauthError('');

    try {
      if (hasPasswordProvider) {
        const currentEmail = firebaseUser.email || user?.email || '';
        if (!currentEmail) {
          throw new Error('Missing account email for password reauthentication.');
        }
        const credential = EmailAuthProvider.credential(currentEmail, reauthPassword);
        await reauthenticateWithCredential(firebaseUser, credential);
      } else {
        await reauthenticateWithPopup(firebaseUser, new GoogleAuthProvider());
      }

      await requestEmailChange(pendingEmailChange);
      setEmailChangeSent(true);
      setEmailChangeDraft('');
      setPendingEmailChange('');
      setShowReauthModal(false);
    } catch (err) {
      if (err instanceof FirebaseError) {
        if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
          setReauthError('Incorrect password. Please try again.');
        } else if (err.code === 'auth/popup-closed-by-user') {
          setReauthError('Sign-in was cancelled. Please try again.');
        } else if (err.code === 'auth/popup-blocked') {
          setReauthError('Popup was blocked by your browser. Please allow popups and try again.');
        } else {
          setReauthError('Could not verify your session. Please try again.');
        }
      } else {
        setReauthError('Could not verify your session. Please try again.');
      }
    } finally {
      setReauthLoading(false);
    }
  };

  const handleDeleteAccountConfirm = async (password?: string) => {
    if (!firebaseUser) return;
    setDeleteAccountError('');
    setDeletingAccount(true);

    try {
      if (hasPasswordProvider) {
        const currentEmail = firebaseUser.email || user?.email || '';
        if (!currentEmail) {
          throw new Error('Missing account email for password reauthentication.');
        }

        const credential = EmailAuthProvider.credential(currentEmail, (password ?? '').trim());
        await reauthenticateWithCredential(firebaseUser, credential);
      }

      await deleteAccount();
      setShowDeleteModal(false);
    } catch (err) {
      setDeletingAccount(false);
      if (err instanceof FirebaseError) {
        if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
          setDeleteAccountError('Incorrect password. Please try again.');
        } else if (err.code === 'auth/requires-recent-login') {
          setDeleteAccountError(t('account.deleteRequiresRecentLogin'));
        } else {
          setDeleteAccountError(t('account.deleteFailed'));
        }
      } else {
        setDeleteAccountError(t('account.deleteFailed'));
      }
    }
  };

  const handleDownloadData = async () => {
    if (!user) return;
    setExporting(true);

    try {
      type CsvRow = {
        section: string;
        record_id: string;
        field: string;
        value: string;
      };

      const rows: CsvRow[] = [];
      const csvEscape = (value: string) => `"${value.replace(/"/g, '""')}"`;
      const pushRow = (section: string, recordId: string, field: string, value: unknown) => {
        rows.push({ section, record_id: recordId, field, value: value == null ? '' : String(value) });
      };

      const flattenRecord = (section: string, recordId: string, value: unknown, path = '') => {
        if (Array.isArray(value)) {
          if (!value.length) {
            pushRow(section, recordId, path || 'value', '');
            return;
          }
          value.forEach((item, index) => flattenRecord(section, recordId, item, path ? `${path}[${index}]` : `[${index}]`));
          return;
        }

        if (value && typeof value === 'object') {
          Object.entries(value as Record<string, unknown>).forEach(([key, nested]) => {
            flattenRecord(section, recordId, nested, path ? `${path}.${key}` : key);
          });
          return;
        }

        pushRow(section, recordId, path || 'value', value);
      };

      const account = {
        id: user.id,
        full_name: user.full_name,
        email: user.email,
        role: user.role,
        profession: user.profession ?? null,
        transplant_date: user.transplant_date ?? null,
        email_verified: !!firebaseUser?.emailVerified,
        created_at: firebaseUser?.metadata?.creationTime ?? null,
        last_sign_in_at: firebaseUser?.metadata?.lastSignInTime ?? null,
      };

      pushRow('meta', 'export', 'schema_version', '1.0');
      pushRow('meta', 'export', 'exported_at', new Date().toISOString());
      flattenRecord('account', user.id, account);
      logs.forEach((log, index) => flattenRecord('logs', log.id || `log-${index + 1}`, log));
      medications.forEach((med, index) => flattenRecord('medications', med.id || `med-${index + 1}`, med));
      contacts.forEach((contact, index) => flattenRecord('contacts', contact.id || `contact-${index + 1}`, contact));
      if (profile) flattenRecord('bean_profile', 'profile', profile);
      xpEvents.forEach((event, index) => flattenRecord('fellowship_xp_events', event.id || `xp-${index + 1}`, event));
      roomieFeed.forEach((entry, index) => flattenRecord('roomie_feed', entry.id || `feed-${index + 1}`, entry));

      const header = ['section', 'record_id', 'field', 'value'];
      const csv = [
        header.join(','),
        ...rows.map(row => [csvEscape(row.section), csvEscape(row.record_id), csvEscape(row.field), csvEscape(row.value)].join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const day = new Date().toISOString().split('T')[0];
      anchor.href = url;
      anchor.download = `kodacare-data-export-${day}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onConfirm={handleDeleteAccountConfirm}
        onCancel={() => {
          if (deletingAccount) return;
          setDeleteAccountError('');
          setShowDeleteModal(false);
        }}
        requirePassword={hasPasswordProvider}
        isLoading={deletingAccount}
        errorMessage={deleteAccountError}
      />

      {showReauthModal && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-3xl border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-[#0d2744] mb-2">Confirm it’s you</h3>
            <p className="text-sm text-gray-600 mb-4">
              For security, please verify your identity to continue changing your email.
            </p>

            {hasPasswordProvider ? (
              <div className="space-y-2 mb-4">
                <label className="block text-xs text-gray-500">Password</label>
                <input
                  type="password"
                  value={reauthPassword}
                  onChange={e => setReauthPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none focus:ring-2 focus:ring-primary-brown/30"
                  placeholder="Enter your password"
                />
              </div>
            ) : (
              <p className="text-sm text-gray-600 mb-4">Continue with Google to verify your session.</p>
            )}

            {reauthError && (
              <div className="mb-4 px-3 py-2 rounded-xl bg-error-red\/5 border border-error-red\/20 text-error-red text-sm">
                {reauthError}
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  if (reauthLoading) return;
                  setShowReauthModal(false);
                  setPendingEmailChange('');
                  setReauthPassword('');
                  setReauthError('');
                }}
                className="text-sm font-semibold text-gray-600 border border-slate-200 hover:bg-slate-50 px-4 py-2 rounded-full transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReauthenticateAndRetry}
                disabled={reauthLoading || (hasPasswordProvider && !reauthPassword.trim())}
                className="text-sm font-semibold text-white bg-primary-brown hover:bg-primary-brown/80 px-4 py-2 rounded-full transition-colors disabled:opacity-60"
              >
                {reauthLoading ? 'Verifying…' : hasPasswordProvider ? 'Verify & Continue' : 'Continue with Google'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SectionLayout title={t('account.security')} description="">
        <section className="bg-white border border-slate-100 rounded-3xl p-7">
          <h2 className="text-lg font-bold text-[#0d2744] mb-5">{t('account.security')}</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-6">
            <div>
              <dt className="text-gray-500">{t('account.memberSince')}</dt>
              <dd className="text-[#0d2744] font-semibold">{createdAt}</dd>
            </div>
            <div>
              <dt className="text-gray-500">{t('account.lastLogin')}</dt>
              <dd className="text-[#0d2744] font-semibold">{lastLoginAt}</dd>
            </div>
          </dl>
          {passwordResetSent && (
            <div className="mb-4 px-4 py-2.5 rounded-2xl bg-green-50 border border-green-200 text-green-700 text-sm">
              {t('account.passwordResetSent')}
            </div>
          )}
          {emailChangeSent && (
            <div className="mb-4 px-4 py-2.5 rounded-2xl bg-green-50 border border-green-200 text-green-700 text-sm">
              Verification email sent. Open your inbox to confirm the new email.
            </div>
          )}
          {emailChangeError && (
            <div className="mb-4 px-4 py-2.5 rounded-2xl bg-error-red\/5 border border-error-red\/20 text-error-red text-sm">
              {emailChangeError}
            </div>
          )}
          <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs text-gray-500 mb-2">Current email</p>
            <p className="text-sm font-semibold text-[#0d2744] mb-3">{firebaseUser?.email || '—'}</p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="email"
                value={emailChangeDraft}
                onChange={e => setEmailChangeDraft(e.target.value)}
                placeholder="new@email.com"
                className="min-w-[220px] flex-1 px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none focus:ring-2 focus:ring-primary-brown/30"
              />
              <button
                onClick={handleChangeEmail}
                disabled={emailChanging || !emailChangeDraft.trim()}
                className="text-sm font-semibold text-[#0d2744] border border-slate-200 hover:bg-slate-100 px-5 py-2 rounded-full transition-colors disabled:opacity-60"
              >
                {emailChanging ? 'Sending…' : 'Change Email'}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleLogout}
              className="text-sm font-semibold text-[#0d2744] border border-slate-200 hover:bg-slate-50 px-5 py-2 rounded-full transition-colors"
            >
              {t('options.logOut')}
            </button>
            <button
              onClick={handleResetPassword}
              className="text-sm font-semibold text-[#0d2744] border border-slate-200 hover:bg-slate-50 px-5 py-2 rounded-full transition-colors"
            >
              {t('account.resetPassword')}
            </button>
            <button
              onClick={() => {
                setDeleteAccountError('');
                setShowDeleteModal(true);
              }}
              disabled={deletingAccount}
              className="text-sm font-semibold text-error-red border border-error-red\/30 bg-error-red\/5 hover:bg-error-red\/10 px-5 py-2 rounded-full transition-colors disabled:opacity-70"
            >
              {deletingAccount ? t('account.deleting') : t('options.deleteAccount')}
            </button>
          </div>
        </section>

        <section className="bg-white border border-slate-100 rounded-3xl p-7">
          <h2 className="text-lg font-bold text-[#0d2744] mb-2">{t('account.dataPrivacy')}</h2>
          <p className="text-sm text-gray-500 mb-5">{t('account.exportDescription')}</p>
          <button
            onClick={handleDownloadData}
            disabled={exporting}
            className="text-sm font-bold bg-[#0d2744] hover:bg-[#1a3a5c] text-white px-5 py-2 rounded-full transition-colors disabled:opacity-70 disabled:cursor-default"
          >
            {exporting ? t('account.preparingExport') : t('account.downloadAllData')}
          </button>
        </section>
      </SectionLayout>
    </>
  );
}

export default function Options() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { section } = useParams();
  const { user } = useAuth();
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedResults, setSeedResults] = useState<Array<{ email: string; status: string }> | null>(null);

  const handleSeedBots = async () => {
    setSeedLoading(true);
    setSeedResults(null);
    try {
      const fns = getFunctions(app, 'europe-west1');
      const seed = httpsCallable<unknown, { results: Array<{ email: string; status: string }> }>(fns, 'seedBotUsers');
      const { data } = await seed({});
      setSeedResults(data.results);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSeedResults([{ email: '—', status: `Error: ${msg}` }]);
    } finally {
      setSeedLoading(false);
    }
  };

  const activeSection = useMemo(() => {
    if (section === 'profile' || section === 'app-preferences' || section === 'subscription' || section === 'security') {
      return section as SettingsSection;
    }
    return undefined;
  }, [section]);

  useEffect(() => {
    if (section && !activeSection) navigate('/settings', { replace: true });
  }, [section, activeSection, navigate]);

  if (activeSection === 'profile') return <ProfileSettings />;
  if (activeSection === 'app-preferences') return <AppPreferencesSettings />;

  if (activeSection === 'subscription') return <SubscriptionSettings />;
  if (activeSection === 'security') return <SecuritySettings />;

  const cards: Array<{ slug: SettingsSection; title: string; description: string; icon: React.ReactNode }> = [
    {
      slug: 'profile',
      title: t('account.profile'),
      description: t('account.profileSubtitle'),
      icon: <UserCircle2 size={22} className="text-primary-brown" />,
    },
    {
      slug: 'app-preferences',
      title: t('options.appPreferences'),
      description: `${t('options.language')} • ${t('options.units')} • ${t('options.timeFormat')} • ${t('guide.category.reminders.title')}`,
      icon: <SlidersHorizontal size={22} className="text-primary-brown" />,
    },
    {
      slug: 'subscription',
      title: t('options.subscription'),
      description: t('options.subscriptionSubtitle'),
      icon: <CreditCard size={22} className="text-primary-brown" />,
    },
    {
      slug: 'security',
      title: t('account.security'),
      description: `${t('account.dataPrivacy')} • ${t('account.resetPassword')}`,
      icon: <Shield size={22} className="text-primary-brown" />,
    },
  ];

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-black text-[#0d2744]">{t('account.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('account.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {cards.map(card => (
          <button
            key={card.slug}
            type="button"
            onClick={() => navigate(`/settings/${card.slug}`)}
            className="w-full text-left bg-white border border-slate-100 rounded-3xl p-6 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-brown/10 flex items-center justify-center shrink-0">
                  {card.icon}
                </div>
                <div>
                  <p className="text-base font-bold text-[#0d2744]">{card.title}</p>
                  <p className="text-sm text-gray-500 mt-1">{card.description}</p>
                </div>
              </div>
              <ChevronRight size={18} className="text-gray-400" />
            </div>
          </button>
        ))}
      </div>

      {user?.is_admin === true && (
        <div className="mt-8 border border-dashed border-slate-300 rounded-3xl p-6 bg-slate-50">
          <p className="text-xs font-mono text-slate-400 mb-3">🤖 ADMIN — Bot Seeding</p>
          <button
            type="button"
            onClick={handleSeedBots}
            disabled={seedLoading}
            className="px-5 py-2.5 rounded-xl bg-[#0d2744] text-white text-sm font-semibold disabled:opacity-50"
          >
            {seedLoading ? 'Seeding…' : 'Seed Bot Users'}
          </button>
          {seedResults && (
            <ul className="mt-4 space-y-1">
              {seedResults.map((r, i) => (
                <li key={i} className="text-xs font-mono text-slate-600">
                  <span className="text-slate-400">{r.email}</span> — {r.status}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

